import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Today's clip — the daily habit hook.
 *
 * Renders on the AthleteDashboard. Fetches a single picked-for-you
 * clip (avoiding ones the athlete has seen in the last 7 days) and
 * shows a thumbnail + AI-generated technique line. Tap → opens the
 * full library page on this clip.
 */
export default function TodaysClipCard({ athleteId, theme }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!athleteId || !token) return;
    setLoading(true);
    axios.get(`${API}/clips/today/${athleteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setClip(r.data?.clip || null))
      .catch(() => setClip(null))
      .finally(() => setLoading(false));
  }, [athleteId, token]);

  if (loading || !clip) return null;

  const player = clip.player?.name || 'A pro';
  const technique = clip.ai_breakdown?.technique || clip.title;

  return (
    <button
      onClick={() => navigate(`/clips?focus=${clip.id}`)}
      className="w-full rounded-2xl overflow-hidden text-left transition-transform hover:-translate-y-0.5"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.10), rgba(0,217,255,0.08))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div className="flex">
        <div
          className="relative shrink-0"
          style={{
            width: 140, height: 100,
            background: '#000',
          }}
        >
          {clip.thumbnail_url ? (
            <img
              src={clip.thumbnail_url}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : null}
          <span
            className="absolute bottom-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}
          >
            {clip.duration_seconds || 30}s
          </span>
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ pointerEvents: 'none' }}
          >
            <span style={{
              width: 38, height: 38, borderRadius: 19,
              background: 'rgba(0,201,167,0.9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#060818', fontSize: 16, fontWeight: 'bold',
            }}>▶</span>
          </div>
        </div>

        <div className="flex-1 p-3 min-w-0">
          <div
            className="text-[10px] uppercase tracking-widest font-bold mb-1"
            style={{ color: '#00C9A7' }}
          >
            ✨ Today's clip
          </div>
          <div className="font-bold text-sm leading-tight truncate">
            {player} — {clip.title}
          </div>
          <div className="text-xs opacity-70 mt-1 line-clamp-2">
            {technique}
          </div>
        </div>
      </div>
    </button>
  );
}
